import React, { type FC, type ReactNode } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';

import { expValEquals } from '@confluence/feature-experiments';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

const styles = cssMap({
	triggerWrapper: {
		cursor: 'pointer',
	},
});

export type AccessRequestProfileCardTriggerProps = {
	/** Atlassian Account ID of the requesting user. When falsy, children render unwrapped. */
	userId: string | null | undefined;
	/** Display name used as the fallback label inside the profile card. */
	displayName: string;
	/** Children to wrap with the profile hover card. */
	children: ReactNode;
	/**
	 * Offset of the profile card popup relative to the trigger, as [x, y] in pixels.
	 * Defaults to no offset. Use e.g. [0, -16] to shift the card upward.
	 */
	offset?: [number, number];
};

/**
 * Wraps `children` in the standard Confluence profile hover card when the
 * `cc_fe_access_request_hover_card` experiment is enabled. When the experiment
 * is disabled or `userId` is missing, renders `children` unchanged so callers
 * can use this component unconditionally.
 */
export const AccessRequestProfileCardTrigger: FC<AccessRequestProfileCardTriggerProps> = ({
	userId,
	displayName,
	children,
	offset,
}) => {
	if (!userId || !expValEquals('cc_fe_access_request_hover_card', 'isEnabled', true)) {
		return <>{children}</>;
	}

	return (
		<Box xcss={styles.triggerWrapper}>
			<ProfileCardWrapper userId={userId} fullName={displayName} trigger="hover" offset={offset}>
				{children}
			</ProfileCardWrapper>
		</Box>
	);
};
